import type { Prototype } from "./api";

export type SortMode = "newest" | "oldest" | "title" | "expiring";

export interface ListView {
  ownerFilter: "all" | "mine" | string;
  currentEmail: string | null;
  search: string;
  selectedTags: string[];
  sort: SortMode;
}

function matchesOwner(p: Prototype, view: ListView): boolean {
  if (view.ownerFilter === "mine") return p.owner === view.currentEmail;
  if (view.ownerFilter && view.ownerFilter !== "all") return p.owner === view.ownerFilter;
  return true;
}

function matchesSearch(p: Prototype, q: string): boolean {
  if (!q) return true;
  const hay = [p.title, p.description ?? "", p.owner, ...(p.tags ?? [])].join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

function matchesTags(p: Prototype, selected: string[]): boolean {
  if (selected.length === 0) return true;
  const tags = p.tags ?? [];
  return selected.some((t: string) => tags.includes(t));
}

function sortValue(p: Prototype, sort: SortMode): number | string {
  switch (sort) {
    case "newest":
    case "oldest":
      return p.uploadedAt;
    case "title":
      return p.title.toLowerCase();
    case "expiring":
      return p.kept || !p.expiresAt ? Number.POSITIVE_INFINITY : p.expiresAt;
  }
}

export function filterAndSortPrototypes(all: Prototype[], view: ListView): Prototype[] {
  const filtered = all.filter(
    (p) => matchesOwner(p, view) && matchesSearch(p, view.search) && matchesTags(p, view.selectedTags),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    const av = sortValue(a, view.sort);
    const bv = sortValue(b, view.sort);
    if (view.sort === "newest") return String(bv).localeCompare(String(av));
    if (view.sort === "oldest") return String(av).localeCompare(String(bv));
    if (view.sort === "title") return String(av).localeCompare(String(bv));
    return (av as number) - (bv as number);
  });
  return sorted;
}
